import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiClient, AuthError, registerUnauthorizedHandler } from "@/lib/api-client";
import type { LoginRedirectState } from "@/lib/login-redirect";

export interface User {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthResponse {
  user: User;
}

/**
 * Provides global authentication state to the entire application.
 * Bootstraps the session by calling GET /auth/me on mount.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A 401 only means "session expired" when there was a session. For a guest it is the
  // normal answer (the startup /auth/me, a wrong password on /login), and redirecting
  // would lock them out of /register and the public info pages.
  const userRef = useRef<User | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Read at 401 time, so the user lands back on the page they were working on.
  const location = useLocation();
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      if (!userRef.current) return;
      userRef.current = null;
      setUser(null);
      queryClient.clear();
      toast.error("Сессия истекла, войдите заново");
      navigate("/login", {
        replace: true,
        state: { from: locationRef.current } satisfies LoginRedirectState,
      });
    });
  }, [navigate, queryClient]);

  useEffect(() => {
    apiClient
      .get<AuthResponse>("/auth/me")
      .then(({ user: currentUser }) => setUser(currentUser))
      .catch((err) => {
        if (!(err instanceof AuthError)) {
          console.error("[AuthProvider] Unexpected error on session check:", err);
        }
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSetUser = useCallback((nextUser: User | null) => {
    setUser(nextUser);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      setUser: handleSetUser,
    }),
    [user, isLoading, handleSetUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Returns the current authentication context.
 * Must be used inside an <AuthProvider>.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
